import { useState } from "react";
import { Button, Chip, Progress, addToast, useDisclosure } from "@heroui/react";
import { Calendar, Plus, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { CustomerSubscriptionCreditModal } from "./CustomerSubscriptionCreditModal";

type CustomerSubscriptionCreditsSectionProps = {
  customerSubscriptionId: number;
};

const formatCreditAppointment = (appointment: {
  startTimeFormatted: string;
  serviceName: string | null;
}) =>
  appointment.serviceName
    ? `${appointment.serviceName}, ${appointment.startTimeFormatted}`
    : appointment.startTimeFormatted;

const getCreditTypeMeta = (creditType: string) => {
  switch (creditType) {
    case "rollover":
      return { label: t`Prenos`, color: "success" } as const;
    case "manual":
      return { label: t`Ročno dodani`, color: "primary" } as const;
    case "makeup":
      return {
        label: t`Nadomestni termin`,
        color: "warning",
      } as const;
    default:
      return { label: creditType, color: "default" } as const;
  }
};

export const CustomerSubscriptionCreditsSection = ({
  customerSubscriptionId,
}: CustomerSubscriptionCreditsSectionProps) => {
  const { data, isPending, processedErrorMessage } =
    api.customer.useGetCustomerSubscription({ customerSubscriptionId });
  const customerSubscription = data?.customerSubscription;

  const {
    isOpen: isCreditModalOpen,
    onOpen: onCreditModalOpen,
    onClose: onCreditModalClose,
  } = useDisclosure();

  const [deletingCreditId, setDeletingCreditId] = useState<number | null>(null);
  const {
    mutateAsync: deleteCredit,
    isPending: isDeletingCredit,
    processedErrorMessage: deleteCreditError,
  } = api.customer.useDeleteCustomerSubscriptionCredit();

  const handleDeleteCredit = async (creditId: number) => {
    try {
      setDeletingCreditId(creditId);
      await deleteCredit({
        customerSubscriptionId,
        creditId,
      });
      addToast({
        title: t`Kredit izbrisan`,
        color: "success",
      });
    } catch {
      addToast({
        title: t`Napaka`,
        description: deleteCreditError,
        color: "danger",
      });
    } finally {
      setDeletingCreditId(null);
    }
  };

  if (isPending) {
    return <LimeLoader />;
  }

  if (!customerSubscription) {
    if (processedErrorMessage) {
      return <p className="text-small text-danger">{processedErrorMessage}</p>;
    }
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p
          data-identifier="subscription-credits-title"
          className="text-medium font-semibold"
        >
          {t`Dodatni obiski`}
        </p>
        <Button
          size="sm"
          variant="bordered"
          onPress={onCreditModalOpen}
          startContent={<Plus className="h-4 w-4" />}
        >
          <Trans>Dodaj dodatni obisk</Trans>
        </Button>
      </div>
      {customerSubscription.credits &&
        customerSubscription.credits.length > 0 &&
        customerSubscription.credits.map((credit) => {
          const remaining = credit.amount - credit.usedAmount;
          const percentage =
            credit.amount > 0 ? (credit.usedAmount / credit.amount) * 100 : 0;
          const isExpired = dayjs(credit.validTo).isBefore(dayjs(), "day");
          const serviceLabel = credit.service?.nameLocalized;
          const creditTypeMeta = getCreditTypeMeta(credit.type);

          return (
            <div
              key={credit.id}
              data-identifier="subscription-credit-item"
              className={`rounded-md border px-3 py-2 ${
                isExpired
                  ? "border-danger-200 bg-danger-50"
                  : "border-default-200"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-semibold">
                      <Trans>
                        {credit.usedAmount}/{credit.amount} porabljenih
                      </Trans>
                    </span>
                    <div className="w-full max-w-[250px] flex-1">
                      <Progress
                        data-identifier="subscription-credit-progress"
                        value={percentage}
                        size="sm"
                        color={isExpired ? "danger" : "primary"}
                      />
                    </div>
                    <span className="text-default-500 text-xs">
                      <Trans>{remaining} preostalih</Trans>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <div
                      className={`flex items-center gap-1 ${
                        isExpired ? "text-danger" : "text-default-500"
                      }`}
                    >
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {isExpired ? t`Poteklo` : t`Veljavno do`}{" "}
                        {credit.validToFormatted}
                      </span>
                    </div>
                    {serviceLabel && (
                      <Chip size="sm" variant="flat" radius="sm">
                        {serviceLabel}
                      </Chip>
                    )}
                    <Chip
                      size="sm"
                      variant="flat"
                      radius="sm"
                      color={creditTypeMeta.color}
                    >
                      {creditTypeMeta.label}
                    </Chip>
                  </div>
                  {credit.sourceAppointment && (
                    <p className="text-default-500 text-xs">
                      <Trans>
                        Ustvarjen ob odpovedi termina{" "}
                        {formatCreditAppointment(credit.sourceAppointment)}
                      </Trans>
                    </p>
                  )}
                  {credit.consumingAppointments.length > 0 && (
                    <div className="space-y-0.5 text-default-500 text-xs">
                      <p>{t`Porabljen za`}:</p>
                      <ul className="list-inside list-disc">
                        {credit.consumingAppointments.map((appointment) => (
                          <li key={appointment.userAppointmentId}>
                            {formatCreditAppointment(appointment)}
                            {appointment.isCancelled && ` (${t`odpovedan`})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {credit.reason && (
                    <p className="text-default-500 text-xs">
                      {t`Opomba`}: {credit.reason}
                    </p>
                  )}
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label={t`Izbriši kredit`}
                  onPress={() => handleDeleteCredit(credit.id)}
                  isLoading={isDeletingCredit && deletingCreditId === credit.id}
                >
                  <Trash2 className="text-default-400 h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      {(!customerSubscription.credits ||
        customerSubscription.credits.length === 0) && (
        <p className="text-default-500 text-xs">
          <Trans>Ni dodatnih obiskov.</Trans>
        </p>
      )}

      <CustomerSubscriptionCreditModal
        isOpen={isCreditModalOpen}
        onClose={onCreditModalClose}
        customerSubscription={customerSubscription}
      />
    </div>
  );
};
